import React, {useCallback, useEffect, useMemo, useState} from "react";
import {VsiBubble, VsiH4, VsiH5,} from "../../../styled/App.styled";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {Card, CardBody, CardHeader, Col, Row} from "reactstrap";
import Grid from "../../../components/Grid";
import PieGrid from "../../../components/PieGrid";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {useNavigate, useSearchParams} from "react-router-dom";
import {genericColors, returnPieGridData, weeksLeftColors} from "../../../utils/chartUtils";
import {clusterUnitDefs, datastoreDefs, hostsDefs, vmDefs} from "../../../utils/gridColumnDefs/virtual/allUnits";
import {formatStockChartConfig} from "../../../utils/Helpers";
import Stockchart from "../../../components/Stockchart";

const pieGridConfig = {
    clusters: {
        chartKeys: [
            'days_left_to_capacity',
            'total_hosts',
            'capacity_constrain_str',
        ],
        agKey: 'num_clusters',
        titles: ["Weeks Left to Capacity", "Total Hosts", "Capacity Constraint"],
    },
    datastores: {
        chartKeys: ['vcenter_name', 'cluster_name', 'num_vms'],
        titles: ["VCenter", "Cluster", "Total VM/LPARs"],
        agKey: 'num_datastore',
    },
    hosts: {
        chartKeys: ['cpu_util_pct', 'memory_util_pct'],
        titles: ["CPU Utilization", "Memory Utilization"],
        agKey: 'num_hosts',
    },
    vms: {
        chartKeys: ['os', 'power_state', 'disk_provision_type'],
        titles: ["Operating System", "Power State", "Provisioning"],
        agKey: 'num_vms',
    }
}

const allUnits = [
    {value: 'clusters', label: 'Clusters', route: 'enterprise_virtual_all_clusters',},
    {value: 'datastores', label: 'Datastores', route: 'enterprise_virtual_all_datastores',},
    {value: 'hosts', label: 'Hosts', route: 'enterprise_virtual_all_esx_hosts',},
    {value: 'vms', label: 'VMs', route: 'enterprise_virtual_all_vms',},
];

const VmCharts = ({data, trend}) => {
    const height = 450;
    const trendConfig = {yAxis: {title: {text: '# of VMs'}}};

    const totalCostDonut = returnPieGridData(data ?? [], ['monthly_total_cost']);

    return <>
        <Col xs={12} lg={8}>
            <CardHeader>
                <VsiH5>{`VMs Utilization Trends`}</VsiH5>
            </CardHeader>
            <Stockchart series={trend}
                        saveParams={{
                            chart: 'util',
                        }}
                        animation={false}
                        height={height}
                        config={trendConfig}/>
        </Col>
        <Col xs={12} lg={4}>
            <CardHeader>
                <VsiH5>{`# VMs by Cost`}</VsiH5>
            </CardHeader>
            <PieGrid
                data={totalCostDonut?.[0] ?? []}
                gridConfig={['Cost', `# of VMs`]}/>
        </Col>

    </>
};

const HostCharts = ({data, trend}) => {
    const {theme} = useVsiAppContext();
    const config = pieGridConfig?.["hosts"];
    const trendConfig = {yAxis: {title: {text: '# of VMs'}}};

    return (
        <>
            <Col xs={12} lg={4}>
                <Card color={theme}>
                    <CardHeader>
                        <VsiH5>{`Hosts by ${config?.titles?.[0]}`}</VsiH5>
                    </CardHeader>
                    <CardBody>
                        <PieGrid data={data?.[0] || []}
                                 gridConfig={[config?.titles?.[0] || '', `# of Hosts`]}/>
                    </CardBody>
                </Card>
            </Col>
            <Col xs={12} lg={4}>
                <Stockchart series={trend}
                            config={trendConfig}/>
            </Col>
            <Col xs={12} lg={4}>
                <Card color={theme}>
                    <CardHeader>
                        <VsiH5>{`Hosts by ${config?.titles?.[1]}`}</VsiH5>
                    </CardHeader>
                    <CardBody>
                        <PieGrid data={data?.[1] || []}
                                 gridConfig={[config?.titles?.[1] || '', `# of Hosts`]}/>
                    </CardBody>
                </Card>
            </Col>
        </>
    )
};

export default function VirtualAllUnits({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();
    const paramsUnit = searchParams.get("unit");

    const [unit, setUnit] = useState(() => allUnits.find(({value}) => paramsUnit === value) || allUnits[0]);
    let navigate = useNavigate();

    const [data, {isLoading, setData}] = useDataWithRoute(`/api/v6/report/${unit.route}_proc?client_id=${client.clientId}&period_id=${periodId}`);
    const [vmTrendData] = useDataWithRoute(`/api/v6/report/enterprise_virtual_vms_trend_proc?client_id=${client.clientId}&period_id=${periodId}`, unit.value === 'vms');
    
    const [filteredData, setFilteredData] = useState([]);

    const activeDef = useMemo(() => {
        switch (unit.value) {
            case 'clusters':
                return clusterUnitDefs;
            case 'datastores':
                return datastoreDefs;
            case 'hosts':
                return hostsDefs;
            case 'vms':
                return vmDefs;
            default:
                return [];
        }
    }, [unit.value]);

    const calcVmTrends = useCallback((filteredData) => {
        const trends = [];
        const dates = [];
        const vmNameLookup = {};
        const fullDateAndUtilizationOrganization = {};

        if (filteredData?.length) {
            filteredData.forEach(datum => {
                if (!vmNameLookup[datum.vm_name]) {
                    vmNameLookup[datum.vm_name] = true;
                }
            });
        }

        vmTrendData?.recordset.forEach(trendDatum => {
            if (!filteredData || vmNameLookup[trendDatum.vm_name]) {
                trends.push(trendDatum);
                if (!dates.includes(trendDatum.collection_date)) {
                    dates.push(trendDatum.collection_date);
                }
                if (!fullDateAndUtilizationOrganization.hasOwnProperty(trendDatum.collection_date)) {
                    fullDateAndUtilizationOrganization[trendDatum.collection_date] = {
                        over_utilized_vms: 0,
                        inrange_utilized_vms: 0,
                        under_utilized_vms: 0,
                    };
                }
                if (trendDatum.vsi_capacity_score >= .65) {
                    fullDateAndUtilizationOrganization[trendDatum.collection_date].over_utilized_vms = fullDateAndUtilizationOrganization[trendDatum.collection_date].over_utilized_vms + 1;
                } else if (trendDatum.vsi_capacity_score < .65 && trendDatum.vsi_capacity_score > .15) {
                    fullDateAndUtilizationOrganization[trendDatum.collection_date].inrange_utilized_vms = fullDateAndUtilizationOrganization[trendDatum.collection_date].inrange_utilized_vms + 1;
                } else if (trendDatum.vsi_capacity_score <= .15) {
                    fullDateAndUtilizationOrganization[trendDatum.collection_date].under_utilized_vms = fullDateAndUtilizationOrganization[trendDatum.collection_date].under_utilized_vms + 1;
                }
            }
        });

        let trendsNew = dates.map(d => {
            return {
                collection_date: d,
                over_utilized_vms: fullDateAndUtilizationOrganization[d].over_utilized_vms,
                inrange_utilized_vms: fullDateAndUtilizationOrganization[d].inrange_utilized_vms,
                under_utilized_vms: fullDateAndUtilizationOrganization[d].under_utilized_vms,
            };
        });

        return trendsNew?.sort?.((a, b) => Date.parse(a.collection_date?.split?.('T')?.[0]) - Date.parse(b.collection_date?.split?.('T')?.[0]));
    }, [vmTrendData]);

    useEffect(() => {
        setFilteredData(data?.recordset)
    }, [data]);

    const renderCharts = useMemo(() => {
        const config = pieGridConfig?.[unit.value];

        if (unit.value === "vms") {
            let vmTrendChartData = formatStockChartConfig(calcVmTrends(filteredData) ?? [],
                [
                    {name: 'Over Utilized (> 64%)', field: 'over_utilized_vms',},
                    {name: 'In Range (16 - 64%)', field: 'inrange_utilized_vms',},
                    {name: 'Under Utilized (< 15%)', field: 'under_utilized_vms',},
                ]);
                
            return <VmCharts data={filteredData}
                             trend={vmTrendChartData.series}
                             />
        } else if (unit.value === "hosts") {
            let hostChartData = returnPieGridData(filteredData || [], config?.chartKeys, config?.agKey);
            let formattedHostTrend = formatStockChartConfig(data?.recordsets?.[1] || [],
                    [
                        {name: '# Hosts > 70% CPU', field: 'num_hosts_over_cpu',},
                        {name: '# Hosts > 90% Memory', field: 'num_hosts_over_memory',},
                    ]);

            return <HostCharts data={hostChartData}
                               trend={formattedHostTrend.series}
                               />
        } else {
            let chartData = returnPieGridData(filteredData || [], config?.chartKeys, config?.agKey);
            return chartData.map((chart, index) => {
                return (
                    <Col key={`pie-grid-all-units-virtual-${index}`} xs={12} lg={4}>
                        <Card color={theme}>
                            <CardHeader>
                                <VsiH5>{`# ${unit.label} by ${config?.titles?.[index]}` || ''}</VsiH5>
                            </CardHeader>
                            <CardBody>
                                <PieGrid key={`pie-grid-all-units-virtual-${index}`}
                                         data={chart || []}
                                         colors={index === 0 && unit.value === 'clusters' ? weeksLeftColors : genericColors}
                                         gridConfig={[config?.titles?.[index] || '', `# of ${unit.label}`]}/>
                            </CardBody>
                        </Card>
                    </Col>
                )
            })
        }
    }, [data, unit, theme, filteredData, calcVmTrends]);

    const onUnitChange = (selectedUnit) => {
        if (selectedUnit !== searchParams.get("unit")) {
            setUnit(selectedUnit);
            setSearchParams({
                unit: selectedUnit.value,
            }, {replace: true});
            setData([])
        }
    };

    const onRowClick = (e) => {
        if (unit.value === "hosts") {
            navigate(`/reports/virtual/host/host-summary?esx_host_name=${encodeURI(e.data.esx_host_name)}&vcenter_name=${encodeURI(e.data.vcenter_name)}&cluster_name=${encodeURI(e.data.cluster_name)}&period_id=${e.data.period_id}`)
        } else if (unit.value === "vms") {
            navigate(`/reports/virtual/vm/vm-summary?vm_name=${encodeURI(e.data.vm_name)}&vcenter_name=${encodeURI(e.data.vcenter_name)}&cluster_name=${encodeURI(e.data.cluster_name)}&esx_host_name=${encodeURI(e.data.esx_host_name)}&period_id=${e.data.period_id}`)
        } else if (unit.value === "clusters") {
            navigate(`/reports/virtual/cluster/cluster-summary?cluster_name=${encodeURI(e.data.cluster_name)}&vcenter_name=${encodeURI(e.data.vcenter_name)}&period_id=${e.data.period_id}`)
        } else if (unit.value === "datastores") {
            navigate(`/reports/virtual/datastore/datastore-summary?datastore_name=${encodeURI(e.data.datastore_name)}&vcenter_name=${encodeURI(e.data.vcenter_name)}&period_id=${e.data.period_id}`)
        }
    };

    const filterChanged = (eventData) => {
        let dataAfterFilter = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if (node.data) {
                dataAfterFilter.push(node.data);
            }
        });
        setFilteredData(dataAfterFilter);
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>All Units - {unit.label}</VsiH4>
            <VsiBubble noAutobot>
                <VsiLabeledSelect isDisabled={isLoading}
                                  isLoading={isLoading}
                                  options={allUnits}
                                  label={'Select Unit'}
                                  onChange={onUnitChange}
                                  defaultValue={unit}/>
            </VsiBubble>
            <VsiBubble autobotName={`${unit.label}UnitCharts`} unit={unit.value} autobotInfo={{
                    data: data?.recordset,
                    config: pieGridConfig?.[unit.value],
                    trendData: data?.recordsets?.[1] ?? [],
                    lines: ['# Hosts > 70% CPU', '# Hosts > 90% Memory'],
                    yAxis: {title: {text: '# of VMs'}}
                }}>
                <Row className={"mb-2"}>
                    { renderCharts }
                </Row>
            </VsiBubble>
            <VsiBubble unit={unit.value}>
                <Grid data={data?.recordset}
                      isLoading={isLoading}
                      columnDefs={activeDef}
                      searchDebounce={250}
                      saveParams={{
                          unit: unit.value,
                      }}
                      gridText={`Double click to see detailed ${unit.value.slice(0, unit.value.length - 1)} reports`}
                      rowDoubleClicked={onRowClick}
                      filterChanged={filterChanged}
                      />
            </VsiBubble>
        </Card>
    )
};
